import { useEffect, useState } from 'react';
import { API_CONFIG } from '../lib/apiConfig';
import { parseCsv } from '../lib/csv';
import { fetchWithCache } from '../lib/fetchWithCache';
import { SdBin, SupplyShedDriveTime, WhpClass } from '../lib/types';
import { resiliencePriorityTier } from '../lib/resiliencePriorityColors';
import resilienceSouthCoastRange90 from '../lib/stubs/resilience-matrix-oregon-south-coast-range-90.json';

// CSV schema (long format, one row per WHP×RD×ownership cell):
//   whp_class,rd_bin,ownership_class,acres
// The hook collapses across ownership to produce the 5×4 matrix and
// separately rolls up the top-tier cells (defined by the canonical
// RESILIENCE_PRIORITY_MATRIX in lib/resiliencePriorityColors.ts) split by
// ownership. "Top tier" means the matrix maps the cell to tier_5 — the
// management policy is documented alongside the matrix.

export interface ResilienceMatrixCell {
  whpClass: WhpClass;
  rdBin: SdBin;
  acres: number;
}

export interface ResiliencePriorityMatrixData {
  matrix: ResilienceMatrixCell[];
  totalShedAcres: number;
  ownership: {
    // Acres mapped to tier_5 by RESILIENCE_PRIORITY_MATRIX (the "top
    // priority" cells per the documented decision matrix).
    priorityAcres: number;
    // Family + corporate share of priorityAcres. Named after the ownership
    // typology in USFS RDS-2025-0045 — these are private timberlands by
    // ownership type, NOT certification status.
    privateAcres: number;
    federalAcres: number;
    // State, local, tribal, and unknown-forest ownership combined — the
    // residual after private and federal.
    otherAcres: number;
  };
  source: 'live' | 'stub';
}

interface Result {
  data: ResiliencePriorityMatrixData | null;
  isLoading: boolean;
  error: string | null;
}

const STUB_REGISTRY: Record<string, ResiliencePriorityMatrixData> = {
  'oregon-south-coast-range:90': {
    matrix: resilienceSouthCoastRange90.matrix as ResilienceMatrixCell[],
    totalShedAcres: resilienceSouthCoastRange90.totalShedAcres,
    ownership: resilienceSouthCoastRange90.ownership,
    source: 'stub',
  },
};

const stubFor = (
  millCode: string | null,
  driveTime: SupplyShedDriveTime,
): ResiliencePriorityMatrixData | null => {
  if (!millCode) return null;
  return STUB_REGISTRY[`${millCode}:${driveTime}`] ?? null;
};

const WHP_CLASSES: WhpClass[] = ['very_low', 'low', 'moderate', 'high', 'very_high'];
const RD_BINS: SdBin[] = ['0_to_30', '30_to_60', '60_to_80', '80_plus'];

// Ownership rollup categories used by the matrix card. Ownership strings in
// the CSV come from generate-fo-data.py / RDS-2025-0045: family, corporate,
// federal, state, local, tribal, unknown_forest. Family + corporate fold
// into "private", federal stays alone, the remainder rolls into "other".
const PRIVATE_OWNERSHIPS = new Set(['family', 'corporate']);

const parseResilienceCsv = (text: string): ResiliencePriorityMatrixData | null => {
  const rows = parseCsv(text);
  if (rows.length === 0) return null;

  const cellAcres = new Map<string, number>();
  let priorityAcres = 0;
  let privateAcres = 0;
  let federalAcres = 0;
  let otherAcres = 0;
  let totalShedAcres = 0;

  for (const row of rows) {
    const whpClass = row['whp_class'] as WhpClass;
    const rdBin = row['rd_bin'] as SdBin;
    const ownership = row['ownership_class'];
    const acres = Number(row['acres']);
    if (!Number.isFinite(acres)) continue;
    if (!WHP_CLASSES.includes(whpClass) || !RD_BINS.includes(rdBin)) continue;

    const key = `${whpClass}:${rdBin}`;
    cellAcres.set(key, (cellAcres.get(key) ?? 0) + acres);
    totalShedAcres += acres;

    const isTopTier = resiliencePriorityTier(whpClass, rdBin) === 'tier_5';
    if (isTopTier) {
      priorityAcres += acres;
      if (PRIVATE_OWNERSHIPS.has(ownership)) {
        privateAcres += acres;
      } else if (ownership === 'federal') {
        federalAcres += acres;
      } else {
        otherAcres += acres;
      }
    }
  }

  const matrix: ResilienceMatrixCell[] = [];
  for (const whpClass of WHP_CLASSES) {
    for (const rdBin of RD_BINS) {
      matrix.push({
        whpClass,
        rdBin,
        acres: cellAcres.get(`${whpClass}:${rdBin}`) ?? 0,
      });
    }
  }

  return {
    matrix,
    totalShedAcres,
    ownership: {
      priorityAcres,
      privateAcres,
      federalAcres,
      otherAcres,
    },
    source: 'live',
  };
};

export const useResiliencePriorityMatrix = (
  millCode: string | null,
  driveTime: SupplyShedDriveTime,
  enabled: boolean = true,
): Result => {
  const [data, setData] = useState<ResiliencePriorityMatrixData | null>(null);
  const [isLoading, setIsLoading] = useState(enabled && millCode !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !millCode) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Short-circuit to the calibrated stub when the (mill, dt) tuple is
    // registered in STUB_REGISTRY. The per-cell crosstab CSV pipeline
    // isn't deployed for these tuples (the priority-overlay PNG is, but
    // the matrix CSV is a separate offline job). Skipping the fetch
    // avoids a guaranteed CloudFront 403 + console-error noise. Remove
    // the entry from STUB_REGISTRY when the CSV ships to re-enable the
    // live fetch path.
    const knownStub = stubFor(millCode, driveTime);
    if (knownStub) {
      setData(knownStub);
      setIsLoading(false);
      setError(null);
      return;
    }

    const url = `${API_CONFIG.s3.baseUrl}/${API_CONFIG.s3.paths.resilienceMatrix(millCode, driveTime)}`;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchWithCache(url, (r) => r.text().then(parseResilienceCsv))
      .then((parsed) => {
        if (cancelled) return;
        if (parsed) {
          setData(parsed);
          setIsLoading(false);
          return;
        }
        // Empty parse — fall back to stub.
        const stub = stubFor(millCode, driveTime);
        setData(stub);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Per-cell crosstab CSV not yet published (the WHP × RD × ownership
        // pipeline is separate from the priority-overlay PNG precompute,
        // which IS deployed). Fall back to the stub when one exists. The
        // card surfaces a "Pilot preview" ribbon while source === 'stub'
        // so the audience sees that the per-cell breakdown is illustrative
        // even when the headline figure is calibrated to match the live
        // overlay's top-tier acres.
        const stub = stubFor(millCode, driveTime);
        if (stub) {
          setData(stub);
          setIsLoading(false);
        } else {
          setError('Resilience matrix not yet available for this shed.');
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [millCode, driveTime, enabled]);

  return { data, isLoading, error };
};
